import { ChevronRight, CircleCheck, Sparkles, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';

type ProfileMenuHeaderProps = {
  name: string | null;
  email?: string;
  role: string;
  organizationName?: string;
  planLabel: string;
  planHint: string;
  hasPlan?: boolean;
  isPendingCheckout?: boolean;
  pendingLabel?: string;
  onPlanClick?: () => void;
};

export function ProfileMenuHeader({
  name,
  email,
  role,
  organizationName,
  planLabel,
  planHint,
  hasPlan = false,
  isPendingCheckout = false,
  pendingLabel,
  onPlanClick,
}: ProfileMenuHeaderProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const nameClass = isDark ? 'text-true-white' : 'text-grey-900';
  const mutedClass = isDark ? 'text-grey-400' : 'text-grey-500';
  const emailClass = name ? mutedClass : nameClass;
  const chipClass = isDark
    ? 'bg-sidebar-pill-dark-hover text-true-white'
    : 'bg-grey-100 text-grey-700';
  const chipBaseClass =
    'inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-2xs font-medium leading-4';
  const planRowClass =
    'mt-2 flex w-full min-w-0 items-center gap-2.5 rounded-field border border-plan-panel-border bg-plan-panel px-2.5 py-2 text-left';

  // The plan row is only a button for people who can change the plan.
  const planRowContent = (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-field bg-plan-upgrade text-grey-950">
        {hasPlan ? (
          <CircleCheck className="size-4" aria-hidden />
        ) : (
          <Sparkles className="size-4" aria-hidden />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'flex min-w-0 items-center gap-1 text-compact font-semibold leading-heading',
            nameClass
          )}
        >
          {isPendingCheckout ? (
            <TriangleAlert className="size-3.5 shrink-0 text-warning" aria-label={pendingLabel} />
          ) : null}
          <span className="truncate">{planLabel}</span>
        </span>
        <span className={cn('mt-0.5 break-words text-2xs font-normal leading-4', mutedClass)}>
          {planHint}
        </span>
      </span>
      {onPlanClick ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="-m-3 inline-flex shrink-0 items-center justify-center p-3"
              aria-label={t('CLICK_TO_PURCHASE_ANOTHER_PLAN', 'Click to purchase another plan')}
            >
              <ChevronRight className={cn('size-4', mutedClass)} aria-hidden />
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">
            {t('CLICK_TO_PURCHASE_ANOTHER_PLAN', 'Click to purchase another plan')}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="border-b border-border px-4 pb-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          {name ? (
            <div className={cn('truncate text-sm font-semibold leading-heading', nameClass)}>
              {name}
            </div>
          ) : null}
          {email ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={cn('truncate text-2xs font-normal leading-4', emailClass)}>
                  {email}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom">{email}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        <div className="mt-2 flex min-w-0 flex-wrap gap-1.5">
          <span className={cn(chipBaseClass, chipClass)}>{role}</span>
          {organizationName ? (
            <span className={cn(chipBaseClass, chipClass)}>{organizationName}</span>
          ) : null}
        </div>

        {onPlanClick ? (
          <button
            type="button"
            onClick={onPlanClick}
            className={cn(planRowClass, 'min-h-11 transition-opacity hover:opacity-90')}
          >
            {planRowContent}
          </button>
        ) : (
          <div className={planRowClass}>{planRowContent}</div>
        )}
      </div>
    </TooltipProvider>
  );
}
